import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { EMAIL_ADDRESS_PATTERN } from './validation-patterns';

/**
 * Enhanced email validator that ensures no spaces in email addresses.
 * This validator is stricter than Angular's built-in email validator.
 * Use this in addition to Validators.email for comprehensive validation.
 * 
 * Pattern matches: user@domain.com, first.last@subdomain.domain.co.uk
 * Rejects: emails with spaces, missing @ or domain extension
 * 
 * @returns ValidatorFn that validates email format without spaces
 * 
 * @example
 * ```typescript
 * this.form = this.fb.group({
 *   email: ['', [Validators.required, Validators.email, noSpaceEmailValidator()]]
 * });
 * ```
 */
export function noSpaceEmailValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null; // Allow empty values (use Validators.required separately)
    }

    const isValid = EMAIL_ADDRESS_PATTERN.test(control.value);

    return isValid ? null : { invalidEmail: { value: control.value } };
  };
}

/**
 * Comprehensive email validator that combines required and no-space validation.
 * 
 * @returns ValidatorFn that validates email is present and properly formatted
 * 
 * @example
 * ```typescript
 * this.form = this.fb.group({
 *   email: ['', [Validators.email, requiredEmailValidator()]]
 * });
 * ```
 */
export function requiredEmailValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value || control.value.trim() === '') {
      return { required: true };
    }

    const isValid = EMAIL_ADDRESS_PATTERN.test(control.value);

    return isValid ? null : { invalidEmail: { value: control.value } };
  };
}

/**
 * RFC 5321 compliant email validator with strict validation rules.
 * 
 * Validation rules:
 * - Structure: Must contain an @ symbol separating the local part and the domain
 * - Local part: Cannot begin or end with a period (.) and cannot exceed 64 characters
 * - Domain: Must have a valid structure (typically including a TLD such as .com) and cannot contain leading or trailing hyphens
 * - Total length: The entire email address cannot exceed 254 characters
 * 
 * @returns ValidatorFn that validates email according to RFC 5321 standards
 * 
 * @example
 * ```typescript
 * this.form = this.fb.group({
 *   email: ['', [Validators.required, strictEmailValidator()]]
 * });
 * ```
 */
export function strictEmailValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    if (!value) {
      return null; // Allow empty values (use Validators.required separately)
    }

    // Rule 1: Total length cannot exceed 254 characters
    if (value.length > 254) {
      return { invalidEmail: { message: 'Email cannot exceed 254 characters' } };
    }

    // Rule 2: Must contain exactly one @ symbol
    const atCount = (value.match(/@/g) || []).length;
    if (atCount !== 1) {
      return { invalidEmail: { message: 'Email must contain exactly one @ symbol' } };
    }

    const parts = value.split('@');
    const localPart = parts[0];
    const domainPart = parts[1];

    // Validate local part
    const localPartError = validateLocalPart(localPart);
    if (localPartError) {
      return localPartError;
    }

    // Validate domain part
    const domainError = validateDomain(domainPart);
    if (domainError) {
      return domainError;
    }

    return null;
  };
}

/**
 * Validates the local part of an email address
 */
function validateLocalPart(localPart: string): ValidationErrors | null {
  if (!localPart || localPart.length === 0) {
    return { invalidEmail: { message: 'Email local part cannot be empty' } };
  }

  if (localPart.length > 64) {
    return { invalidEmail: { message: 'Email local part cannot exceed 64 characters' } };
  }

  if (localPart.startsWith('.') || localPart.endsWith('.')) {
    return { invalidEmail: { message: 'Email local part cannot begin or end with a period' } };
  }

  if (localPart.includes('..')) {
    return { invalidEmail: { message: 'Email local part cannot contain consecutive periods' } };
  }

  if (localPart.startsWith('-')) {
    return { invalidEmail: { message: 'Email local part cannot begin with a hyphen' } };
  }

  const localPartPattern = /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~.-]+$/;
  if (!localPartPattern.test(localPart)) {
    return { invalidEmail: { message: 'Email local part contains invalid characters' } };
  }

  return null;
}

/**
 * Validates the domain part of an email address
 */
function validateDomain(domainPart: string): ValidationErrors | null {
  if (!domainPart || domainPart.length === 0) {
    return { invalidEmail: { message: 'Email domain cannot be empty' } };
  }

  if (!domainPart.includes('.')) {
    return { invalidEmail: { message: 'Email domain must include a top-level domain (e.g., .com)' } };
  }

  if (domainPart.startsWith('-') || domainPart.endsWith('-')) {
    return { invalidEmail: { message: 'Email domain cannot begin or end with a hyphen' } };
  }

  if (domainPart.startsWith('.') || domainPart.endsWith('.')) {
    return { invalidEmail: { message: 'Email domain cannot begin or end with a period' } };
  }

  // Validate domain labels
  const domainLabels = domainPart.split('.');
  const labelError = validateDomainLabels(domainLabels);
  if (labelError) {
    return labelError;
  }

  // TLD validation
  const tld = domainLabels[domainLabels.length - 1];
  if (tld.length < 2 || !/^[A-Za-z]+$/.test(tld)) {
    return { invalidEmail: { message: 'Top-level domain must be at least 2 letters' } };
  }

  return null;
}

/**
 * Validates domain labels (parts separated by dots)
 */
function validateDomainLabels(labels: string[]): ValidationErrors | null {
  for (const label of labels) {
    if (label.length === 0) {
      return { invalidEmail: { message: 'Email domain cannot contain consecutive periods' } };
    }
    if (label.startsWith('-') || label.endsWith('-')) {
      return { invalidEmail: { message: 'Email domain labels cannot begin or end with a hyphen' } };
    }
    if (!/^[A-Za-z0-9-]+$/.test(label)) {
      return { invalidEmail: { message: 'Email domain labels can only contain letters, numbers, and hyphens' } };
    }
  }
  return null;
}